import buildStockLedgerWorkbook from './buildStockLedgerWorkbook';

// CSV headers arrive as "Date", "Product Name", "Quantity In", ... — normalize
// them to the snake_case keys buildStockLedgerWorkbook expects.
const HEADER_KEY_MAP = { brand: 'brand_name' };

export const normalizeLedgerHeader = header => {
  const key = header.trim().toLowerCase().replace(/\s+/g, '_');
  return HEADER_KEY_MAP[key] ?? key;
};

export const parseLedgerCsv = async csvText => {
  const { default: Papa } = await import('papaparse');
  const { data, errors } = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: normalizeLedgerHeader,
  });
  // Papa.parse still returns partial data on malformed input (mismatched
  // columns, unterminated quotes); never build a workbook from it
  if (errors.length > 0) {
    throw new Error(`Malformed ledger CSV: ${errors[0].message}`);
  }
  return data;
};

// Downloads the export job's CSV file and saves it as the two-sheet
// (Summary / Detail) Excel workbook. Returns false when the CSV is empty.
const downloadStockLedgerXlsx = async fileUrl => {
  const response = await fetch(fileUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch ledger CSV: ${response.status}`);
  }
  const csvText = await response.text();

  const rows = await parseLedgerCsv(csvText);
  if (!rows.length) return false;

  // the export job does not carry its query range, so derive it from the data
  const dates = rows
    .map(row => row.date)
    .filter(Boolean)
    .sort();
  const startDate = dates[0];
  const endDate = dates[dates.length - 1];

  const workbook = await buildStockLedgerWorkbook(rows, {
    startDate,
    endDate,
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `inventory_ledger_${startDate}_${endDate}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
  return true;
};

export default downloadStockLedgerXlsx;
